'use client'

import { useEffect, useMemo, useState } from 'react'

import { Check, ChevronsUpDown } from 'lucide-react'

import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue'
import { useOptimizedRecords } from '@/shared/hooks/useOptimizedQuery'
import { cn } from '@/shared/lib/utils'
import { selectClassName } from '@/shared/ui/input-styles'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'

interface SchoolOption {
  id: number
  name: string
}

interface SchoolAsyncSelectProps {
  value: number | ''
  onChange: (value: number | '') => void
  hasError?: boolean
  disabled?: boolean
  id?: string
}

const PER_PAGE = 20

export function SchoolAsyncSelect({
  value,
  onChange,
  hasError,
  disabled,
  id
}: SchoolAsyncSelectProps) {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const debouncedTerm = useDebouncedValue(term, 300)

  // Keeps the label of every school we've seen so the selected school's name
  // stays resolvable even after the search results change.
  const [labels, setLabels] = useState<Map<number, string>>(new Map())

  const { records: schools, isLoading } = useOptimizedRecords<SchoolOption>({
    path: '/public/schools',
    params: {
      q: { name_cont: debouncedTerm.trim() },
      page: 1,
      per_page: PER_PAGE
    }
  })

  useEffect(() => {
    if (!schools.length) return
    setLabels(prev => {
      const next = new Map(prev)
      schools.forEach(school => next.set(school.id, school.name))
      return next
    })
  }, [schools])

  const selectedLabel = value === '' ? undefined : labels.get(value)

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setTerm('')
  }

  const handleSelect = (school: SchoolOption) => {
    setLabels(prev => new Map(prev).set(school.id, school.name))
    onChange(school.id)
    handleOpenChange(false)
  }

  const emptyMessage = useMemo(() => {
    if (isLoading) return 'Carregando...'
    return 'Nenhuma escola encontrada.'
  }, [isLoading])

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            selectClassName,
            'flex items-center justify-between gap-2 text-left',
            hasError && 'ring-2 ring-red-500'
          )}
        >
          <span className={cn('truncate', !selectedLabel && 'text-text-gray/60')}>
            {selectedLabel ?? 'Selecione uma escola'}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] rounded-xl p-0"
      >
        <div className="border-b p-2">
          <input
            autoFocus
            value={term}
            onChange={event => setTerm(event.target.value)}
            placeholder="Buscar escola..."
            className="w-full rounded-lg border bg-transparent px-3 py-2 text-sm outline-none focus-visible:border-primary"
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {schools.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {emptyMessage}
            </div>
          ) : (
            schools.map(school => (
              <button
                key={school.id}
                type="button"
                onClick={() => handleSelect(school)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition hover:bg-muted',
                  value === school.id && 'bg-muted'
                )}
              >
                <span className="truncate">{school.name}</span>
                {value === school.id && (
                  <Check className="size-4 shrink-0 text-primary" />
                )}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
